import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { AccountProvider } from "../context/AccountContext";
import { useAuth } from "../context/AuthContext";

import { MainContent } from "./MainContent";
import { useResponsive } from "../hooks/useResponsive";
import { MobileContent } from "./MobileContent";
import { ViewProvider } from "../context/ViewContext";
import { useGlobalKeyboardShortcuts } from "../hooks/useGlobalKeyboardShortcuts";
import { useVersion } from "../hooks/useVersion";
import WelcomeModal from "../components/Modal/WelcomeModal";
import WhatsNewModal from "../components/Modal/WhatsNewModal";
import PWAInstallPrompt from "../components/PWA/PWAInstallPrompt";

export const MainLayout = () => {
  const { isAuthenticated } = useAuth();
  const { isMobile } = useResponsive();
  const location = useLocation();
  const { appVersion, hasSignificantUpdate } = useVersion();

  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);
  const [whatsNewVersion, setWhatsNewVersion] = useState(null);

  // Initialize global keyboard shortcuts
  useGlobalKeyboardShortcuts();

  // Check for first access or version changes
  useEffect(() => {
    if (isAuthenticated) {
      // Check for first access
      const isFirstAccess =
        localStorage.getItem("firstAccess") === "true";

      if (isFirstAccess) {
        setShowWelcomeModal(true);
        // Remove the flag so it doesn't show again
        localStorage.removeItem("firstAccess");
        return; // Don't show both modals at once
      }

      // Check for version updates
      if (hasSignificantUpdate) {
        setWhatsNewVersion(appVersion);
        setShowWhatsNewModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
  };

  const handleWhatsNewModalClose = () => {
    setShowWhatsNewModal(false);
    setWhatsNewVersion(null);
  };

  if (!isAuthenticated) {
    // Preserve URL parameters when redirecting to signin
    const searchParams = new URLSearchParams(location.search);
    const redirectPath =
      searchParams.get("plan") === "pro"
        ? `/signin?plan=pro&redirect=${encodeURIComponent(
            location.pathname + location.search
          )}`
        : `/signin?redirect=${encodeURIComponent(
            location.pathname + location.search
          )}`;

    return <Navigate to={redirectPath} replace />;
  }

  return (
    <AccountProvider>
      <ViewProvider>
        {isMobile ? (
          <MobileContent>
            <Outlet />
          </MobileContent>
        ) : (
          <MainContent>
            <Outlet />
          </MainContent>
        )}
      </ViewProvider>

      {/* Welcome Modal for first-time users - only render when needed */}
      {showWelcomeModal && (
        <WelcomeModal
          show={showWelcomeModal}
          onHide={handleWelcomeModalClose}
        />
      )}

      {/* What's New Modal for version updates - only render when needed */}
      {showWhatsNewModal && (
        <WhatsNewModal
          show={showWhatsNewModal}
          onHide={handleWhatsNewModalClose}
          version={whatsNewVersion}
        />
      )}

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </AccountProvider>
  );
};
